import React, { useState, useEffect } from 'react';
import { Sparkles, MapPin, Users, Percent, Info, Tag, Plus, Trash2 } from 'lucide-react';
import { Drawer } from '../common/Drawer';
import { Button } from '../common/Button';
import { CustomSelect } from '../common/CustomSelect';
import { VerticalTabs, type VerticalTab } from '../common/VerticalTabs';
import { useDatabase } from '../../db/DatabaseProvider';
import { useBranches, useServiceCategories, useBaseCurrency, useLookupMutations } from '../../db/hooks/useLookupData';
import { useToast } from '../common/ToastProvider';
import { generateId, now } from '../../lib/uuid';
import type { ServiceDocType, PriceTier } from '../../db/schemas';

interface ServiceFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  service?: ServiceDocType | null;
}

export function ServiceFormDrawer({ isOpen, onClose, service }: ServiceFormDrawerProps) {
  const db = useDatabase();
  const { branches } = useBranches();
  const { serviceCategories } = useServiceCategories();
  const { addServiceCategory } = useLookupMutations();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('basic');

  const [concept, setConcept] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [currencyPrice, setCurrencyPrice] = useState<number | ''>('');
  const [commissionPercentage, setCommissionPercentage] = useState<number | ''>('');
  const [serviceBranchId, setServiceBranchId] = useState('');
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (service) {
        setConcept(service.concept);
        setCategoryId(service.category_id || '');
        setCategoryName('');
        setIsCustomCategory(false);
        setCurrencyPrice(service.currency_price);
        setCommissionPercentage(service.commission_percentage);
        setServiceBranchId(service.branch_id || '');
        setDurationMinutes(service.duration_minutes || 30);
        setDescription(service.description || '');
        setPriceTiers(Array.isArray(service.price_tiers) ? [...service.price_tiers] : []);
      } else {
        setConcept('');
        setCategoryId(serviceCategories?.[0]?.id || '');
        setIsCustomCategory(false);
        setCategoryName('');
        setCurrencyPrice('');
        setCommissionPercentage(10);
        setServiceBranchId('');
        setDurationMinutes(30);
        setDescription('');
        setPriceTiers([]);
      }
      setActiveTab('basic');
    }
  }, [isOpen, service, serviceCategories]);

  const handleAddEscalon = () => {
    setPriceTiers([
      ...priceTiers,
      { quantity: 3, label: '+x3', total_price: typeof currencyPrice === 'number' ? currencyPrice * 2.5 : 0 }
    ]);
  };

  const handleRemoveEscalon = (index: number) => {
    setPriceTiers(priceTiers.filter((_, i) => i !== index));
  };

  const handleUpdateEscalon = (index: number, updates: Partial<PriceTier>) => {
    setPriceTiers(priceTiers.map((item, i) => i === index ? { ...item, ...updates } : item));
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!db) return;

    if (!concept.trim()) {
      setActiveTab('basic');
      toast.error('El nombre del servicio es obligatorio.', 'Campo Requerido');
      return;
    }

    if (isCustomCategory && !categoryName.trim()) {
      setActiveTab('basic');
      toast.error('Por favor, ingresa el nombre de la nueva categoría.', 'Categoría Requerida');
      return;
    }

    const price = typeof currencyPrice === 'number' ? currencyPrice : 0;
    const comm = typeof commissionPercentage === 'number' ? commissionPercentage : 0;
    const dur = typeof durationMinutes === 'number' ? durationMinutes : 30;

    if (currencyPrice === '' || price < 0) {
      setActiveTab('basic');
      toast.error('Ingresa un precio válido para el servicio.', 'Precio Inválido');
      return;
    }

    if (comm < 0 || comm > 100) {
      setActiveTab('comisiones');
      toast.error('El porcentaje de comisión debe estar entre 0% y 100%.', 'Comisión Inválida');
      return;
    }

    const timestamp = now();

    try {
      let finalCatId: string | null = categoryId || null;
      let finalCatName: string | null = null;

      if (isCustomCategory && categoryName.trim()) {
        const newCat = await addServiceCategory({ 
          name: categoryName.trim(), 
          color: '#F97316', 
          sort_order: serviceCategories.length,
        });
        if (newCat) {
          finalCatId = newCat.id;
          finalCatName = newCat.name;
        }
      } else if (finalCatId) {
        const cat = serviceCategories.find(c => c.id === finalCatId);
        finalCatName = cat?.name || null;
      }

      if (service) {
        const doc = await db.services.findOne({ selector: { id: service.id } }).exec();
        if (doc) {
          await doc.patch({
            concept: concept.trim(),
            category_id: finalCatId,
            currency_price: price,
            commission_percentage: comm,
            branch_id: serviceBranchId || null,
            duration_minutes: dur,
            description: description.trim() || null,
            is_active: true,
            price_tiers: priceTiers,
            updated_at: timestamp,
          });
        }
      } else {
        const newServId = generateId('serv');
        await db.services.insert({
          id: newServId,
          concept: concept.trim(),
          category_id: finalCatId,
          currency_price: price,
          commission_percentage: comm,
          branch_id: serviceBranchId || null,
          duration_minutes: dur,
          description: description.trim() || null,
          is_active: true,
          price_tiers: priceTiers,
          created_at: timestamp,
          updated_at: timestamp,
          is_deleted: false,
        });
      }
      toast.success(service ? 'Servicio actualizado correctamente' : 'Servicio guardado exitosamente');
      onClose();
    } catch (err: any) {
      toast.error('Error al guardar servicio: ' + (err?.message || 'Error desconocido'));
    }
  };

  const tabs: VerticalTab[] = [
    { id: 'basic', label: 'Información básica', icon: Info },
    { id: 'escalones', label: 'Precios por Volumen (+xN)', icon: Tag },
    { id: 'sedes', label: 'Sedes', icon: MapPin },
    { id: 'staff', label: 'Personal', icon: Users },
    { id: 'comisiones', label: 'Comisiones', icon: Percent },
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={service ? 'Editar servicio' : 'Nuevo servicio'}
      subtitle={service ? 'Actualiza los detalles del servicio' : 'Añade un nuevo servicio a tu catálogo'}
      icon={Sparkles}
      maxWidth="3xl"
      sidebar={
        <div className="bg-surface-secondary/50 p-2 rounded-2xl border border-border">
          <VerticalTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" type="submit" form="service-form" onClick={handleSave}>
            Guardar
          </Button>
        </div>
      }
    >
      <form id="service-form" onSubmit={handleSave} className="space-y-6 max-w-lg">
            
            {activeTab === 'basic' && (
              <div className="space-y-4 animate-fade-in">
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1">
                    Nombre del Servicio *
                  </label>
                  <input
                    type="text"
                    required
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    placeholder="Ej: Depilación Láser Axilas"
                    className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1">
                    Categoría *
                  </label>
                  {!isCustomCategory ? (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <CustomSelect
                          value={categoryId}
                          onChange={(val) => {
                            if (val === '__custom__') {
                              setIsCustomCategory(true);
                              setCategoryName('');
                            } else {
                              setCategoryId(val);
                            }
                          }}
                          options={[
                            { value: '', label: 'Sin categoría' },
                            ...serviceCategories.map((cat) => ({ value: cat.id, label: cat.name })),
                            { value: '__custom__', label: '+ Crear Nueva Categoría' },
                          ]}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        required
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        placeholder="Ej: Facial, Corporal"
                        className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomCategory(false);
                          setCategoryId(serviceCategories?.[0]?.id || '');
                        }}
                        className="px-3 py-2 text-xs font-bold text-text-secondary hover:bg-surface-hover rounded-xl border border-border cursor-pointer shrink-0"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1">
                      Precio ({currencySymbol}) *
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      required
                      min="0"
                      value={currencyPrice}
                      onChange={(e) => setCurrencyPrice(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      placeholder="0.00"
                      className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1">
                      Duración (minutos) *
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="1"
                      required
                      min="1"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      placeholder="30"
                      className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1">
                    Descripción (Opcional)
                  </label>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Agrega detalles sobre el servicio..."
                    className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs resize-none"
                  />
                </div>
              </div>
            )}

            {activeTab === 'escalones' && (
              <div className="space-y-4 animate-fade-in">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-text-primary">Precios Preferenciales por Volumen (+xN)</h4>
                    <p className="text-xs text-text-secondary">Configura ofertas rápidas por comprar múltiples sesiones del mismo servicio.</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddEscalon}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Añadir Escalón
                  </Button>
                </div>

                {priceTiers.length === 0 ? (
                  <div className="p-6 bg-surface-secondary border border-dashed border-border rounded-xl text-center">
                    <Tag className="w-8 h-8 text-text-tertiary mx-auto mb-2 opacity-50" />
                    <p className="text-sm font-semibold text-text-secondary">Sin escalones de volumen</p>
                    <p className="text-xs text-text-tertiary mt-1">
                      Añade opciones como <strong>+x3</strong> o <strong>+x6</strong> para ofrecer packages de sesiones con discount.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {priceTiers.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2.5 items-center p-3 bg-surface-secondary border border-border rounded-xl">
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-[10px] font-bold text-text-tertiary uppercase mb-0.5">Etiqueta</label>
                          <input
                            type="text"
                            value={item.label}
                            onChange={(e) => handleUpdateEscalon(idx, { label: e.target.value })}
                            placeholder="Ej: + 5x"
                            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:border-brand-sky outline-none font-bold"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <label className="block text-[10px] font-bold text-text-tertiary uppercase mb-0.5">Sesiones</label>
                          <input
                            type="number"
                            inputMode="decimal"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleUpdateEscalon(idx, { quantity: parseInt(e.target.value, 10) || 1 })}
                            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:border-brand-sky outline-none font-bold"
                          />
                        </div>

                        <div className="col-span-10 sm:col-span-6">
                          <label className="block text-[10px] font-bold text-text-tertiary uppercase mb-0.5">Precio Total ({currencySymbol})</label>
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={item.total_price ?? 0}
                            onChange={(e) => handleUpdateEscalon(idx, { total_price: parseFloat(e.target.value) || 0 })}
                            className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:border-brand-sky outline-none font-bold"
                          />
                        </div>

                        <div className="col-span-2 sm:col-span-1 flex justify-end pt-3 sm:pt-0">
                          <button
                            type="button"
                            onClick={() => handleRemoveEscalon(idx)}
                            className="text-text-tertiary hover:text-error transition-colors p-1.5 cursor-pointer rounded-lg hover:bg-surface-tertiary"
                            title="Eliminar escalón"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sedes' && (
              <div className="space-y-4 animate-fade-in">
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1">
                    Sucursal Asignada
                  </label>
                  <CustomSelect
                    value={serviceBranchId}
                    onChange={setServiceBranchId}
                    options={[
                      { value: '', label: 'Todas las Sucursales (Global)' },
                      ...branches.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                  />
                  <p className="text-xs text-text-tertiary mt-2">
                    Si seleccionas una sucursal específica, este servicio solo estará disponible para ella. De lo contrario, estará disponible en todas las branches.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'staff' && (
              <div className="space-y-4 animate-fade-in">
                <div className="p-4 bg-surface-secondary border border-border rounded-xl">
                  <p className="text-sm text-text-secondary text-center">
                    Todos los miembros del staff pueden realizar este servicio por defecto.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'comisiones' && (
              <div className="space-y-4 animate-fade-in">
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1">
                    Comisión Personal (%) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.5"
                      required
                      min="0"
                      max="100"
                      value={commissionPercentage}
                      onChange={(e) => setCommissionPercentage(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      placeholder="10"
                      className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs pr-8"
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary pointer-events-none" />
                  </div>
                  <p className="text-xs text-text-tertiary mt-2">
                    Porcentaje de comisión que recibirá el especialista por realizar este servicio.
                  </p>
                </div>
              </div>
            )}

          </form>
    </Drawer>
  );
}
